<script>
import api from '@/api'
import { alertSuccess } from '@/helpers/swal'
import vSelect from 'vue-select'
import 'vue-select/dist/vue-select.css'
import loaderBlack from '@/assets/images/loader-black.svg'
import loaderWhite from '@/assets/images/loader-white.svg'

export default {
  data() {
    return {
      pagetitle: 'Device Types',
      interfaces: [],
      selectedInterfaces: [],
      deviceType: {
        id: null,
        deviceType: '',
        description: '',
        details: null,
        cpuCount: 0,
        tags: ['ezeha'],
      },
      errors: {
        interfaces: null,
      },
      saveBtnDisabled: false,
      loadingInterfaces: true,
      loaderBlack,
      loaderWhite,
    }
  },
  components: {
    vSelect,
  },
  async mounted() {
    // In case of edit device type
    const typeId = this.$route.params.id
    if (typeId) {
      await this.getDeviceType(typeId)
    }
    await this.getInterfaces()
  },
  methods: {
    async getInterfaces() {
      const typeId = this.$route.params.id
      await api
        .get('/influx/interfaces/by-device-type')
        .then((response) => {
          this.interfaces = response.data[typeId] || []
        })
        .finally(() => {
          this.loadingInterfaces = false
        })
    },

    async getDeviceType(typeId) {
      await api.get(`/device-types/${typeId}`).then((response) => {
        let deviceType = response.data?.deviceType || {}
        this.deviceType.id = deviceType.id
        this.deviceType.deviceType = deviceType.deviceType
        this.deviceType.description = deviceType.description
        this.selectedInterfaces = deviceType.details?.interfaces || []
        this.deviceType.cpuCount = deviceType.cpuCount
        this.deviceType.tags = deviceType.tags?.length ? deviceType.tags : ['ezeha']
      })
    },

    saveDeviceType() {
      if (!this.validateRequest()) return

      this.saveBtnDisabled = true
      let data = { ...this.deviceType }
      delete data.tags // tags are read-only for now
      data.interfaces = this.selectedInterfaces.join(',')

      this.deviceType.id ? this.updateDeviceType(data) : this.addDeviceType(data)
    },

    addDeviceType(data) {
      api
        .post('/device-types/add-device-type', data)
        .then(() => {
          alertSuccess('Device type added successfully')
          this.$router.push({ name: 'device-types' })
        })
        .finally(() => {
          this.saveBtnDisabled = false
        })
    },

    updateDeviceType(data) {
      api
        .post(`/device-types/${this.deviceType.id}/edit`, null, {
          params: data,
        })
        .then(() => {
          alertSuccess('Device type updated successfully')
          this.$router.push({ name: 'device-types' })
        })
        .finally(() => {
          this.saveBtnDisabled = false
        })
    },

    validateRequest() {
      let isValid = true
      this.errors = {}

      if (!this.selectedInterfaces.length) {
        this.errors.interfaces = 'Interfaces field is required'
        isValid = false
      }
      return isValid
    },
  },
}
</script>

<template>
  <div>
    <Teleport to="#elePageTitle">
      {{ pagetitle }}
    </Teleport>
    <div class="row">
      <div class="col-xxl-6">
        <div class="card">
          <div class="card-header d-flex align-items-center">
            <h4 class="card-title">{{ $route.params.id ? 'Edit Device Type' : 'Add New Type' }}</h4>
          </div>
          <div class="card-body">
            <form @submit.prevent="saveDeviceType()">
              <div class="row">
                <div class="col-md-12 mb-3">
                  <label for="device-type-input" class="form-label">Device Type</label>
                  <input type="text" id="device-type-input" class="form-control" :value="deviceType.deviceType" disabled />
                </div>
                <div class="col-md-12 mb-3">
                  <label for="description-input" class="form-label">Description</label>
                  <input type="text" id="description-input" class="form-control" :value="deviceType.description" disabled />
                </div>
                <div class="col-md-12 mb-3">
                  <label for="tags-input" class="form-label">Tags</label>
                  <input
                    type="text"
                    id="tags-input"
                    class="form-control"
                    :value="(deviceType.tags || []).join(', ')"
                    disabled
                  />
                  <small class="text-muted">
                    Product tags (read-only). Example: <code>ezeha</code> enables Device Role on the Analytics Dashboard.
                  </small>
                </div>

                <div class="col-md-12 mb-3">
                  <hr class="my-2" />
                  <div class="d-flex align-items-center gap-2 mb-2">
                    <span class="badge bg-primary">Interfaces</span>
                    <span class="text-muted small">Separate from Tags — used for network graphs on Analytics</span>
                  </div>
                  <label for="interface-input" class="form-label">Interfaces</label>
                  <v-select v-model="selectedInterfaces" :options="interfaces" :closeOnSelect="false" multiple placeholder="Select interfaces" :disabled="loadingInterfaces">
                    <template #open-indicator="{ attributes, isOpen }">
                      <template v-if="loadingInterfaces">
                        <img v-bind="attributes" :src="$store.state.layout.layoutMode === 'light' ? loaderBlack : loaderWhite" alt="" style="width: 26px; height: 26px" />
                      </template>
                      <template v-else>
                        <i class="font-size-22 fw-semibold bx" :class="isOpen ? 'bx-chevron-up' : 'bx-chevron-down'" v-bind="attributes"></i>
                      </template>
                    </template>
                  </v-select>
                  <div v-if="errors.interfaces" class="invalid-feedback">{{ errors.interfaces }}</div>
                </div>
                <div class="col-md-12 mb-3">
                  <label for="cpuCount-input" class="form-label">CPU Count</label>
                  <input type="number" id="cpuCount-input" class="form-control" v-model="deviceType.cpuCount" oninput="this.value = this.value.replace(/[^0-9]/g, '')" min="0" />
                </div>
              </div>
            </form>
          </div>
          <div class="card-footer">
            <div class="row">
              <div class="col-7">
                <router-link to="/device/types" class="btn btn-light d-inline-flex align-items-center">
                  <i class="bx bx-arrow-back font-size-16 me-1"></i>
                  Back to List
                </router-link>
              </div>
              <div class="col-5 d-flex justify-content-end">
                <button class="btn btn-success d-flex align-items-center" @click.prevent="saveDeviceType()" :disabled="saveBtnDisabled || loadingInterfaces">
                  <i v-if="!saveBtnDisabled" class="bx bx-check-double font-size-16"></i>
                  <i v-else class="bx bx-loader bx-spin font-size-16"></i>
                  <span class="ms-1">Save</span>
                </button>
              </div>
            </div>
          </div>
        </div>
        <!-- end card -->
      </div>
    </div>
  </div>
</template>
<style scoped>
/* ::v-deep(.vs__selected),
::v-deep(.vs__dropdown-option) {
  text-transform: none !important;
} */
::v-deep(.vs__selected),
::v-deep(.vs__dropdown-option) {
  text-transform: none !important;
}

::v-deep(.vs__dropdown-option--selected) {
  color: #1263dd;        /* dark blue */
  font-weight: 600;
  background-color: transparent !important;
}

::v-deep(.vs__dropdown-option--highlight) {
  filter: brightness(1.15);
}
</style>
